import React, { useEffect, useState } from "react";
import styled from "styled-components";
import CardIcon from '@mui/icons-material/AddCardSharp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SecurityIcon from '@mui/icons-material/Security';
import { Button, Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useFormStore from "../state/useFormStore";
import { config } from "../config";

const CardButton = styled(Button)`
  width: 100%;
  padding: 16px !important;
  text-align: left !important;
  margin-bottom: 16px !important;
  border: 2px solid #1e6373 !important;
  background-color: #95d0dd !important;
  color: #000 !important;
  border-radius: 12px !important;
  transition: all 0.2s ease !important;

  &:hover {
    background-color: #5ab1c4 !important;
    border-color: #1e6373 !important;
    opacity: 0.9;
  }

  &:disabled {
    background-color: #ccc !important;
    border-color: #ccc !important;
  }
`;

const IconWrapper = styled.div`
  margin-right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #000;
  border-radius: 50%;
  color: white;
`;

const OptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  width: 100%;
  max-width: 500px;
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const TextWrapper = styled.div`
  text-align: left;
`;

const ButtonTitle = styled(Typography)`
  font-size: 16px !important;
  font-weight: 600 !important;
  margin-bottom: 4px !important;
`;

const ButtonSubtitle = styled(Typography)`
  font-size: 12px !important;
  color: #666 !important;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
`;

const InfoIcon = styled.div`
  margin-top: 3px;
  color: #95d0dd;
`;

const MetodoPago: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [sdkLoaded, setSdkLoaded] = useState<boolean>(false);
  const navigate = useNavigate();

  const hash = useFormStore((state) => state.hash) || localStorage.getItem("hash");
  const { first_name, last_name, cuil, email, bank, employment, phone } = useFormStore(state => ({
    first_name: state.first_name,
    last_name: state.last_name,
    cuil: state.cuil,
    email: state.email,
    bank: state.bank,
    employment: state.employment,
    phone: state.phone,
  }));
  const formDataString = localStorage.getItem("formData");
  const formData = JSON.parse(formDataString || "{}");

  useEffect(() => {
    if (!document.getElementById("cirenio-payments-sdk") && config.sdk.payments.url) {
      const script = document.createElement("script");
      script.id = "cirenio-payments-sdk";
      script.src = config.sdk.payments.url;
      script.async = true;
      script.onload = () => {
        console.log("SDK de Cirenio cargado correctamente.");
        setSdkLoaded(true);
      };
      document.body.appendChild(script);
    } else {
      setSdkLoaded(true);
    }
  }, []);

  const iniciarMetodoPago = () => {
    if (!sdkLoaded || !(window as any).CirenioPayments) {
      console.error("Error: SDK aún no está disponible.");
      return;
    }

    setLoading(true);

    const CirenioPayments = new (window as any).CirenioPayments(
      config.sdk.payments.accessKey,
      cuil,
      {
        showCloseButton: true,
        externalId: hash,
        firstName: first_name,
        lastName:last_name,
        email: email,
        phone: phone,
        bank: bank,
      }
    );

    // Captura todos los eventos posibles y los muestra en consola
    const eventos = [
      "widget_start",
      "tokenization_success",
      "tokenization_failure",
      "widget_closed",
    ];

    eventos.forEach((evento) => {
      CirenioPayments.on(evento, (data: any) => {
        console.log(`Evento: ${evento}`, data);
      });
    });

    // Evento cuando la tokenización de la tarjeta es exitosa
    CirenioPayments.on("tokenization_success", (data: any) => {
      console.log("Tokenización exitosa:", data);
      CirenioPayments.closeWidget();
      setLoading(false);
      // navigate(`/${hash}/buscando`);
      navigate(`/${hash}/biometria`);
    });

    // Evento cuando hay un fallo en la tokenización
    CirenioPayments.on("tokenization_failure", (error: any) => {
      console.error("Error en la tokenización:", error);
      setLoading(false);
    });

    // Evento cuando el usuario cierra el widget manualmente
    CirenioPayments.on("widget_closed", () => {
      console.log("Widget de pago cerrado por el usuario.");
      setLoading(false);
    });

    CirenioPayments.openWidget();
  };

  return (
    <div className="flex flex-col items-center pt-5">
      <Box mt={5} mb={5}>
        <InfoItem>
          <InfoIcon><CreditCardIcon /></InfoIcon>
          <Box>
            <Typography fontWeight={600}>Requisitos importantes</Typography>
            <Typography variant="body2" mt={1}>
              <CheckCircleIcon fontSize="small" sx={{ color: "#00c851", verticalAlign: "middle" }} /> Debe ser una tarjeta de débito a tu nombre
              <br />
              <CheckCircleIcon fontSize="small" sx={{ color: "#00c851", verticalAlign: "middle" }} /> Vinculada a tu cuenta sueldo
              <br />
              <CheckCircleIcon fontSize="small" sx={{ color: "#00c851", verticalAlign: "middle" }} /> Debe tener un saldo mínimo de $50 disponible
            </Typography>
          </Box>
        </InfoItem>
    
        <InfoItem>
          <InfoIcon><SecurityIcon /></InfoIcon>
          <Box>
            <Typography fontWeight={600}>Datos protegidos</Typography>
            <Typography variant="body2" mt={1}>
              Tus datos están protegidos con los más altos estándares de seguridad bancaria.
            </Typography>
          </Box>
        </InfoItem>

        <InfoItem>
          <InfoIcon><CheckCircleIcon /></InfoIcon>
          <Box>
            <Typography fontWeight={600}>Validación de cuenta</Typography>
            <Typography variant="body2" mt={1}>
              Durante el proceso de vinculación, se realizará una validación automática de tu cuenta con un cargo temporal de hasta $50 que será devuelto inmediatamente. Este proceso es necesario para verificar que la tarjeta esté activa y funcional.
            </Typography>
          </Box>
        </InfoItem>
      </Box>

      <OptionWrapper>
      <CardButton 
        variant="outlined" 
        onClick={iniciarMetodoPago}
        disabled={loading}
      >
        <ButtonContent>
          <IconWrapper>
            <CardIcon />
          </IconWrapper>
          <TextWrapper>
            <ButtonTitle>{loading ? "Procesando..." : "Agregar tarjeta"}</ButtonTitle>
            <ButtonSubtitle>Forma rápida y segura</ButtonSubtitle>
          </TextWrapper>
        </ButtonContent>
      </CardButton>
      </OptionWrapper>
    </div>
  );
};

export default MetodoPago;